/**
 * One booking, with only the actions policy actually permits.
 *
 * The available actions come from the server, which is also what enforces
 * them — the page never guesses.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ConfirmDialog, Dialog } from "@/components/Dialog";
import { Icon } from "@/components/Icon";
import { ErrorState, Notice, SkeletonRows } from "@/components/States";
import { StatusPill } from "@/components/StatusPill";
import { ApiError, api } from "@/lib/api";
import {
  durationBetween,
  timeZoneOf,
  formatDuration,
  formatLongDate,
  formatTime,
  sourceLabel,
  statusLabel,
  statusTone,
} from "@/lib/format";
import { useToast } from "@/lib/toast";
import type { Booking } from "@/lib/types";

const localZone = timeZoneOf(null);

export function BookingDetail() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { notify } = useToast();
  const queryClient = useQueryClient();

  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelScope, setCancelScope] = useState<"occurrence" | "series">("occurrence");
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendMinutes, setExtendMinutes] = useState(15);
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const query = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => api.get<Booking>(`/portal/bookings/${bookingId}`),
    enabled: Boolean(bookingId),
  });

  const booking = query.data;
  const timezone = booking?.room_timezone ?? null;

  function afterChange(message: string) {
    notify(message);
    setActionError(null);
    queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
    queryClient.invalidateQueries({ queryKey: ["portal-bookings"] });
  }

  const checkIn = useMutation({
    mutationFn: () => api.post(`/portal/bookings/${bookingId}/check-in`),
    onSuccess: () => afterChange("Checked in. The room is yours."),
    onError: (error) => setActionError(error instanceof ApiError ? error : null),
  });

  const extend = useMutation({
    mutationFn: () =>
      api.post<{ new_end_at: string }>(`/portal/bookings/${bookingId}/extend`, {
        minutes: extendMinutes,
      }),
    onSuccess: (result) => {
      setExtendOpen(false);
      afterChange(`Extended until ${formatTime(result.new_end_at, timezone)}.`);
    },
    onError: (error) => setActionError(error instanceof ApiError ? error : null),
  });

  const release = useMutation({
    mutationFn: () => api.post(`/portal/bookings/${bookingId}/release`),
    onSuccess: () => {
      setConfirmRelease(false);
      afterChange("The room is free again.");
    },
    onError: (error) => setActionError(error instanceof ApiError ? error : null),
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.del(`/portal/bookings/${bookingId}`, { scope: cancelScope }),
    onSuccess: () => {
      setConfirmCancel(false);
      notify(cancelScope === "series" ? "The series was cancelled." : "The booking was cancelled.");
      navigate("/bookings");
    },
    onError: (error) => {
      setConfirmCancel(false);
      setActionError(error instanceof ApiError ? error : null);
    },
  });

  if (query.isPending) {
    return (
      <div className="page page--narrow">
        <SkeletonRows rows={5} height={64} />
      </div>
    );
  }
  if (query.isError || !booking) {
    return (
      <div className="page page--narrow">
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const actions = booking.available_actions ?? [];
  const durationMinutes = durationBetween(booking.start_at, booking.end_at);
  const maxExtend = booking.policy?.max_extension_minutes ?? 60;
  const remainingExtend = Math.max(maxExtend - booking.extended_minutes, 0);

  return (
    <div className="page page--narrow">
      <Link to="/bookings" className="row" style={{
        gap: "var(--space-2)", color: "var(--ink-secondary)",
        textDecoration: "none", marginBottom: "var(--space-4)",
      }}>
        <Icon name="chevronLeft" size={16} />
        <span className="t-label">All bookings</span>
      </Link>

      <div className="page__head">
        <div className="page__title">
          <div className="row" style={{ gap: "var(--space-3)", flexWrap: "wrap" }}>
            <h1 className="t-h1">{booking.subject}</h1>
            <StatusPill tone={statusTone(booking.status)}>
              {statusLabel(booking.status)}
            </StatusPill>
          </div>
          <p className="t-body ink-secondary">
            {booking.room_name}
            {booking.location_name ? ` · ${booking.location_name}` : ""}
            {booking.room_capacity ? ` · ${booking.room_capacity} seats` : ""}
          </p>
        </div>
      </div>

      <div className="stack" style={{ gap: "var(--space-5)" }}>
        {actionError && (
          <Notice
            tone={actionError.isConflict ? "warning" : "error"}
            title={actionError.isConflict ? "That is not possible right now" : "Something went wrong"}
          >
            {actionError.message}
          </Notice>
        )}

        {booking.status === "pending_approval" && (
          <Notice tone="info" title="Waiting for approval">
            An administrator needs to approve this booking. The room is not held until they
            do, so it may be taken by someone else in the meantime.
          </Notice>
        )}

        {booking.sync.status === "failed" && (
          <Notice tone="warning" title="Not yet added to the connected calendar">
            The room is booked in AlphaRoom and is being held for you. The calendar entry is
            still being retried and an administrator has been notified.
          </Notice>
        )}

        {booking.status === "no_show" && (
          <Notice tone="warning" title="This room was released">
            Nobody checked in within the grace period, so the room was handed back for
            others to use.
          </Notice>
        )}

        <div className="card">
          <div className="card__body">
            <div className="detail-list">
              <div className="detail-list__row">
                <span className="detail-list__key">When</span>
                <span className="detail-list__value">
                  {formatLongDate(booking.start_at, timezone)}
                  <br />
                  <span className="t-mono">
                    {formatTime(booking.start_at, timezone)}–{formatTime(booking.end_at, timezone)}
                  </span>{" "}
                  <span className="ink-tertiary">({formatDuration(durationMinutes)})</span>
                  {timezone && timezone !== localZone && (
                    <span className="ink-tertiary"> · {timezone}</span>
                  )}
                  {booking.extended_minutes > 0 && (
                    <span className="ink-tertiary"> · extended by {booking.extended_minutes} min</span>
                  )}
                </span>
              </div>
              <div className="detail-list__row">
                <span className="detail-list__key">Organiser</span>
                <span className="detail-list__value">
                  {booking.organizer?.display_name ?? booking.guest_name ?? "—"}
                </span>
              </div>
              {booking.booked_for && booking.booked_for.id !== booking.organizer?.id && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Booked for</span>
                  <span className="detail-list__value">{booking.booked_for.display_name}</span>
                </div>
              )}
              {booking.created_by && booking.created_by.id !== booking.organizer?.id && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Booked by</span>
                  <span className="detail-list__value">{booking.created_by.display_name}</span>
                </div>
              )}
              <div className="detail-list__row">
                <span className="detail-list__key">Booked from</span>
                <span className="detail-list__value">{sourceLabel(booking.source)}</span>
              </div>
              {booking.checked_in_at && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Checked in</span>
                  <span className="detail-list__value">
                    {formatTime(booking.checked_in_at, timezone)}
                  </span>
                </div>
              )}
              {booking.attendees.length > 0 && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Attendees</span>
                  <span className="detail-list__value">{booking.attendees.join(", ")}</span>
                </div>
              )}
              {booking.meeting_link && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Meeting link</span>
                  <a className="detail-list__value" href={booking.meeting_link}
                    target="_blank" rel="noreferrer">
                    Join
                  </a>
                </div>
              )}
              {booking.description && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Notes</span>
                  <span className="detail-list__value">{booking.description}</span>
                </div>
              )}
              {booking.cancel_reason && (
                <div className="detail-list__row">
                  <span className="detail-list__key">Reason</span>
                  <span className="detail-list__value">{booking.cancel_reason}</span>
                </div>
              )}
            </div>
          </div>

          {actions.length > 0 && (
            <div className="card__footer" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
              {actions.includes("check_in") && (
                <button className="btn btn--primary" onClick={() => checkIn.mutate()}
                  disabled={checkIn.isPending}>
                  <Icon name="check" size={16} />
                  {checkIn.isPending ? "Checking in…" : "Check in"}
                </button>
              )}
              {actions.includes("extend") && (
                <button className="btn btn--secondary" onClick={() => setExtendOpen(true)}>
                  <Icon name="clock" size={16} />
                  Extend
                </button>
              )}
              {actions.includes("release") && (
                <button className="btn btn--secondary" onClick={() => setConfirmRelease(true)}>
                  <Icon name="stop" size={16} />
                  Finish early
                </button>
              )}
              <span className="grow" />
              {actions.includes("cancel") && (
                <button className="btn btn--ghost" onClick={() => {
                  setCancelScope("occurrence");
                  setConfirmCancel(true);
                }}>
                  Cancel booking
                </button>
              )}
            </div>
          )}
        </div>

        {booking.policy?.explanation?.length ? (
          <section className="card">
            <div className="card__header">
              <h2 className="t-h3">Rules for this room</h2>
              <span className="t-caption ink-tertiary">{booking.policy.name}</span>
            </div>
            <div className="card__body">
              <div className="detail-list">
                {booking.policy.explanation.map((item) => (
                  <div className="detail-list__row" key={item.label}>
                    <span className="detail-list__key">{item.label}</span>
                    <span className="detail-list__value t-body ink-secondary">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {booking.series_id && (
          <Notice tone="info" title="Part of a repeating series">
            Changes here affect this date only unless you choose to cancel the whole series.
            {actions.includes("cancel") && (
              <button className="btn btn--ghost btn--compact" style={{ marginTop: 8 }}
                onClick={() => { setCancelScope("series"); setConfirmCancel(true); }}>
                Cancel the whole series
              </button>
            )}
          </Notice>
        )}
      </div>

      <Dialog
        open={extendOpen}
        title="Extend this meeting"
        description={`The room is yours until ${formatTime(booking.end_at, timezone)}.`}
        onClose={() => setExtendOpen(false)}
        footer={
          <>
            <button className="btn btn--secondary" onClick={() => setExtendOpen(false)}>
              Cancel
            </button>
            <button className="btn btn--primary" onClick={() => extend.mutate()}
              disabled={extend.isPending || remainingExtend === 0}>
              {extend.isPending
                ? "Extending…"
                : `Extend to ${formatTime(
                    new Date(new Date(booking.end_at).getTime() + extendMinutes * 60000).toISOString(),
                    timezone,
                  )}`}
            </button>
          </>
        }
      >
        <div className="stack" style={{ gap: "var(--space-4)" }}>
          {remainingExtend === 0 ? (
            <Notice tone="warning" title="This booking cannot be extended further">
              It has already been extended by the maximum {maxExtend} minutes for this room.
            </Notice>
          ) : (
            <>
              <p className="t-body ink-secondary">
                You can add up to {remainingExtend} more minutes, if the room stays free.
              </p>
              <div className="chips">
                {[15, 30, 45, 60].filter((minutes) => minutes <= remainingExtend).map((minutes) => (
                  <button key={minutes} type="button" className="chip"
                    aria-pressed={extendMinutes === minutes}
                    onClick={() => setExtendMinutes(minutes)}>
                    +{minutes} min
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </Dialog>

      <ConfirmDialog
        open={confirmRelease}
        title="Finish early and free the room?"
        message={`${booking.room_name} becomes available immediately, and anyone can book the rest of your slot.`}
        confirmLabel="Free the room"
        destructive
        busy={release.isPending}
        onConfirm={() => release.mutate()}
        onClose={() => setConfirmRelease(false)}
      />

      <ConfirmDialog
        open={confirmCancel}
        title={cancelScope === "series" ? "Cancel the whole series?" : "Cancel this booking?"}
        message={
          cancelScope === "series"
            ? "Every remaining occurrence is cancelled. Meetings that have already happened are kept for the record."
            : `${booking.room_name} on ${formatLongDate(booking.start_at, timezone)} will be released.`
        }
        confirmLabel={cancelScope === "series" ? "Cancel series" : "Cancel booking"}
        destructive
        busy={cancel.isPending}
        onConfirm={() => cancel.mutate()}
        onClose={() => setConfirmCancel(false)}
      />
    </div>
  );
}
